"use client";

import { useState, useRef, useCallback } from "react";
import { Mic, Square, Loader2 } from "lucide-react";

interface VoiceRecorderProps {
  onRecordingComplete: (blob: Blob) => void;
  disabled?: boolean;
}

export default function VoiceRecorder({ onRecordingComplete, disabled }: VoiceRecorderProps) {
  const [recording, setRecording] = useState(false);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  const startRecording = useCallback(async () => {
    setError(null);
    setStarting(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported("audio/webm")
          ? "audio/webm"
          : "audio/mp4",
      });
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mediaRecorder.mimeType });
        onRecordingComplete(blob);
        stream.getTracks().forEach((track) => track.stop());
      };

      mediaRecorder.start();
      setRecording(true);
    } catch (err) {
      if (err instanceof DOMException) {
        switch (err.name) {
          case "NotAllowedError":
            setError("Microphone access denied. Please allow microphone access in your browser settings.");
            break;
          case "NotFoundError":
            setError("No microphone found. Please connect a microphone and try again.");
            break;
          case "AbortError":
            // User cancelled, no need to show error
            break;
          case "SecurityError":
            setError("Microphone access requires a secure connection (HTTPS).");
            break;
          default:
            setError("Could not access microphone. Please try again.");
        }
      } else {
        setError("Could not access microphone. Please try again.");
      }
    } finally {
      setStarting(false);
    }
  }, [onRecordingComplete]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
      mediaRecorderRef.current.stop();
      setRecording(false);
    }
  }, []);

  return (
    <div className="flex flex-col items-center gap-3">
      <button
        type="button"
        onClick={recording ? stopRecording : startRecording}
        disabled={disabled || starting}
        className={`flex h-16 w-16 items-center justify-center rounded-full transition-all active:scale-95 ${
          recording
            ? "animate-pulse bg-red-500 text-white shadow-lg shadow-red-500/40"
            : "bg-primary text-primary-foreground shadow-md hover:shadow-lg"
        } ${disabled || starting ? "opacity-50 cursor-not-allowed" : ""}`}
        aria-label={recording ? "Stop recording" : "Start recording"}
      >
        {starting ? (
          <Loader2 className="h-7 w-7 animate-spin" />
        ) : recording ? (
          <Square className="h-7 w-7" />
        ) : (
          <Mic className="h-7 w-7" />
        )}
      </button>
      <p className="text-sm text-muted-foreground">
        {starting ? "Starting..." : recording ? "Tap to stop" : "Tap to speak"}
      </p>
      {error && (
        <p className="text-sm text-destructive text-center">{error}</p>
      )}
    </div>
  );
}
